import React, { useEffect, useMemo, useRef, useState } from "react";
import { GT_progressBarAndTasksGapInPx, GT_taskCardHeight } from "./index";
import { TbClock } from "react-icons/tb";
import {
  calculateWorkingDaysBetweenDates,
  getOrgWorkingDays,
  isOrgWeekend,
} from "../../utils/helperUtils";
import moment from "moment";
import { EntityViewTooltip } from "../../features/EntityViewTooltip/EntityViewTooltip";
import { ViewType } from "./types";
import { useSelector } from "react-redux";
import Xarrow, { useXarrow } from "react-xarrows";
import { uuid } from "html-to-image/es/util";
import {
  DashboardStatus,
  dashboardStatusHexColors,
  getDashboardStatusLabel,
} from "../../constants/integrationTypes";
import { FiAlertTriangle } from "react-icons/fi";
import { useResourceInfo } from "../../hooks/useResourceInfo";
import CustomTooltip from "../../components/common/CustomTooltip";

interface TaskProps {
  orgId: string;
  task: any;
  index: number;
  startDate: string;
  handleTaskClick: (task: any) => void;
  isTaskHighlighted: boolean;
  viewType: ViewType;
  spacing: number;
}

const GTTask: React.FC<TaskProps> = ({
  orgId,
  task,
  index,
  handleTaskClick,
  isTaskHighlighted,
  startDate,
  viewType,
  spacing,
}): React.ReactElement => {
  const updateXArrow = useXarrow();
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({
    placement: "bottom",
    x: 0,
    y: 0,
  });
  const [isTooltipHovered, setIsTooltipHovered] = useState(false);
  const [copiedItem, setCopiedItem] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const showTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { getResourceName } = useResourceInfo();

  useEffect(() => {
    updateXArrow();
  }, [viewType]);

  const { orgSchedule: organizationHolidays = [] } = useSelector(
    (state: any) => state.organization,
  );

  const organizationWorkingDays = getOrgWorkingDays(orgId);

  const { simulation, currentEntityId } = useSelector(
    (state: any) => state.timeline,
  );
  const { template_id } = useSelector((state: any) => state.templates);
  const isTemplate = !!template_id;

  const calculateWorkingDaysOffset = () => {
    if (viewType === "weekly") {
      const adjustedStartDate = moment(startDate)
        .subtract(2, "days")
        .startOf("day");
      let taskStartMoment = moment(task.start).startOf("day");

      while (true) {
        const currentDate = taskStartMoment.format("YYYY-MM-DD");
        const isWeekend = isOrgWeekend(currentDate, organizationWorkingDays);
        const isHoliday = organizationHolidays.some(
          (h: { date: string }) => h.date.split("T")[0] === currentDate,
        );

        if (!isWeekend && !isHoliday) {
          break;
        }
        taskStartMoment.add(1, "day");
      }

      let workingDays = 0;
      const current = moment(adjustedStartDate);

      while (current.isSameOrBefore(taskStartMoment)) {
        const currentDate = current.format("YYYY-MM-DD");
        const isHoliday = organizationHolidays.some(
          (h: { date: string }) => h.date.split("T")[0] === currentDate,
        );
        if (!isOrgWeekend(currentDate, organizationWorkingDays) && !isHoliday) {
          workingDays++;
        }
        current.add(1, "day");
      }

      return { length: Math.max(0, workingDays - 1) }; // Subtract 1 to fix off-by-one
    }

    if (viewType === "monthly") {
      let taskStartMoment = moment(task.start).startOf("day");
      const baselineStart = moment(startDate)
        .subtract(7, "days")
        .startOf("day");

      while (true) {
        const currentDate = taskStartMoment.format("YYYY-MM-DD");
        const isWeekend = isOrgWeekend(currentDate, organizationWorkingDays);
        const isHoliday = organizationHolidays.some(
          (h: { date: string }) => h.date.split("T")[0] === currentDate,
        );

        if (!isWeekend && !isHoliday) {
          break;
        }
        taskStartMoment.add(1, "day");
      }

      let workingDays = 0;
      const current = moment(baselineStart);

      while (current.isSameOrBefore(taskStartMoment)) {
        const currentDate = current.format("YYYY-MM-DD");
        const isHoliday = organizationHolidays.some(
          (h: { date: string }) => h.date.split("T")[0] === currentDate,
        );
        if (!isOrgWeekend(currentDate, organizationWorkingDays) && !isHoliday) {
          workingDays++;
        }
        current.add(1, "day");
      }

      return { length: Math.max(0, workingDays - 1) }; // Subtract 1 to fix off-by-one
    }

    if (viewType === "yearly") {
      const taskStart = moment(task.start);
      const taskEnd = moment(task.end);
      const taskYearStart = moment(taskStart).startOf("year");

      const startDayOfYear = taskStart.diff(taskYearStart, "days");
      const endDayOfYear = taskEnd.diff(taskYearStart, "days");
      const taskDuration = endDayOfYear - startDayOfYear + 1; // +1 to include both start and end days

      return { length: taskDuration };
    }

    if (viewType === "quarterly") {
      const taskStart = moment(task.start).startOf("day");
      const projectStartMonth = moment(startDate).startOf("month");

      let workingDays = 0;
      const current = moment(projectStartMonth);

      while (current.isSameOrBefore(taskStart)) {
        const currentDate = current.format("YYYY-MM-DD");
        const isWeekend = isOrgWeekend(currentDate, organizationWorkingDays);
        const isHoliday = organizationHolidays.some(
          (h: { date: string }) => h.date.split("T")[0] === currentDate,
        );

        if (!isWeekend && !isHoliday) {
          workingDays++;
        }
        current.add(1, "day");
      }

      return { length: Math.max(0, workingDays - 1) }; // Subtract 1 to align with grid
    }

    const workingDays = calculateWorkingDaysBetweenDates(
      startDate,
      task.start,
      organizationWorkingDays,
      organizationHolidays,
    );
    return { length: workingDays };
  };

  const calculateTaskWidth = () => {
    const workingDays = calculateWorkingDaysBetweenDates(
      task.start,
      task.end,
      organizationWorkingDays,
      organizationHolidays,
    );
    return { length: workingDays };
  };

  const workingDaysToStart = calculateWorkingDaysOffset();
  const workingDaysInTask = calculateTaskWidth();

  let taskPosLeftInPx = 0;
  let taskWidth = 0;

  if (viewType === "yearly") {
    const taskStart = moment(task.start).startOf("day");
    const taskEnd = moment(task.end).startOf("day");
    const rawStart = moment(startDate);
    const baselineStart =
      rawStart.month() >= 6
        ? rawStart.clone().startOf("year").add(6, "months")
        : rawStart.clone().startOf("year");

    const offsetDays = Math.max(0, taskStart.diff(baselineStart, "days"));
    const taskDuration = taskEnd.diff(taskStart, "days") + 1; // include both start and end

    taskPosLeftInPx = Math.floor(offsetDays * spacing);
    taskWidth = Math.floor(taskDuration * spacing);
  } else {
    taskPosLeftInPx = Math.floor(workingDaysToStart.length * spacing);
    taskWidth = Math.floor(workingDaysInTask.length * spacing);
  }

  const delayedTaskPosLeftInPx = task?.delayed_duration
    ? taskPosLeftInPx - task.delayed_duration * spacing
    : taskPosLeftInPx;

  // Animation for date changes in simulation similar to GTProject's DateShiftBadges
  const isSimNew = Boolean(
    simulation && task?.sim_flag && !task?.live_start && !task?.live_end,
  );
  const isSimUpdated = Boolean(simulation && task?.isUpdated);

  const calculateTooltipPosition = (event?: MouseEvent) => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Tooltip dimensions (approximate)
    const tooltipWidth = 300;
    const tooltipHeight = 200; // Approximate height
    const margin = 46; // Margin from viewport edges

    let placement = "top"; // Default to top (above the bar)
    let x = 0;

    // Get cursor position if event is provided
    if (event) {
      // Calculate relative position within the container
      x = event.clientX - containerRect.left;

      // Ensure tooltip doesn't go off-screen horizontally and doesn't cross the sidebar edge
      const sidebarElement = document.getElementById("roadmap-sidebar");
      const sidebarRightEdge = sidebarElement
        ? sidebarElement.getBoundingClientRect().right
        : 0;

      const tooltipLeft = containerRect.left + x - tooltipWidth / 2;
      const tooltipRight = tooltipLeft + tooltipWidth;

      // Minimum allowed left based on viewport margin and sidebar edge
      const minAllowedLeft = Math.max(margin, sidebarRightEdge + margin);

      if (tooltipLeft < minAllowedLeft) {
        x = minAllowedLeft - containerRect.left + tooltipWidth / 2;
      } else if (tooltipRight > viewportWidth - margin) {
        // Clamp right side
        x =
          viewportWidth -
          margin -
          tooltipWidth -
          containerRect.left +
          tooltipWidth / 2;
      }
    } else {
      // Fallback to center if no event
      x = containerRect.width / 2;
    }

    // Check space above (preferred for tooltips)
    const spaceAbove = containerRect.top;
    const spaceBelow = viewportHeight - containerRect.bottom;

    // Check if tooltip would be overlapped by timeline navbar or other UI elements
    const safeTopZone = 400; // Very conservative estimate to avoid all clipping
    const tooltipTopPosition = containerRect.top - tooltipHeight - margin;

    // Force tooltips below for upper portion of screen to avoid any clipping
    // Check multiple conditions: tooltip position, container position, and upper screen area
    if (
      tooltipTopPosition < safeTopZone ||
      containerRect.top < safeTopZone ||
      containerRect.top < viewportHeight * 0.3
    ) {
      // Upper 30% of screen
      placement = "bottom";
    } else if (spaceAbove >= tooltipHeight + margin) {
      placement = "top";
    } else if (spaceBelow >= tooltipHeight + margin) {
      placement = "bottom";
    } else {
      // Not enough space above or below, use the side with more space
      placement = spaceAbove > spaceBelow ? "top" : "bottom";
    }

    // When event is not provided, ensure tooltip respects sidebar edge and viewport margins
    if (!event) {
      const sidebarElementGlobal = document.getElementById("roadmap-sidebar");
      const sidebarRightEdgeGlobal = sidebarElementGlobal
        ? sidebarElementGlobal.getBoundingClientRect().right
        : 0;
      const minAllowedLeftGlobal = Math.max(
        margin,
        sidebarRightEdgeGlobal + margin,
      );
      const tooltipLeftGlobal = containerRect.left + x - tooltipWidth / 2;
      const tooltipRightGlobal = tooltipLeftGlobal + tooltipWidth;

      if (tooltipLeftGlobal < minAllowedLeftGlobal) {
        x = minAllowedLeftGlobal - containerRect.left + tooltipWidth / 2;
      } else if (tooltipRightGlobal > viewportWidth - margin) {
        x =
          viewportWidth -
          margin -
          tooltipWidth -
          containerRect.left +
          tooltipWidth / 2;
      }
    }

    setTooltipPosition({ placement, x, y: 0 });
  };

  const handleMouseEnter = (event: React.MouseEvent) => {
    // Clear any existing hide timer
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }

    // Clear any existing show timer to prevent multiple timers
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }

    // Set timer to show tooltip after 500ms
    showTimerRef.current = setTimeout(() => {
      setShowTooltip(true);
      setTimeout(() => {
        calculateTooltipPosition(event.nativeEvent);
      }, 0);
      showTimerRef.current = null;
    }, 500);
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (showTooltip && !isTooltipHovered) {
      calculateTooltipPosition(event.nativeEvent);
    }
  };

  const handleMouseLeave = () => {
    // Clear any existing show timer
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }

    // Clear any existing hide timer to prevent multiple timers
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }

    // Set timer to hide tooltip after 200ms
    hideTimerRef.current = setTimeout(() => {
      setShowTooltip(false);
      setIsTooltipHovered(false);
      hideTimerRef.current = null;
    }, 200);
  };

  useEffect(() => {
    const handleResize = () => {
      if (showTooltip) {
        calculateTooltipPosition();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      // Clean up timers on unmount
      if (showTimerRef.current) {
        clearTimeout(showTimerRef.current);
      }
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, [showTooltip]);

  useEffect(() => {
    const durationMs = 600; // match CSS transition
    let raf: number | undefined;
    const start = performance.now();
    const step = (now: number) => {
      updateXArrow();
      if (now - start < durationMs) {
        raf = requestAnimationFrame(step);
      }
    };
    raf = requestAnimationFrame(step);
    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [
    simulation,
    taskPosLeftInPx,
    taskWidth,
    delayedTaskPosLeftInPx,
    viewType,
    index,
  ]);

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(type);
      setTimeout(() => setCopiedItem(null), 2000); // Reset after 2 seconds
      console.log(`Copied ${type}: ${text}`);
    } catch (err) {
      console.error("Failed to copy: ", err);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopiedItem(type);
      setTimeout(() => setCopiedItem(null), 2000); // Reset after 2 seconds
    }
  };

  const truncateMiddle = (str: string, maxLength: number = 20) => {
    if (str.length <= maxLength) return str;
    const start = Math.ceil(maxLength / 3);
    const end = Math.floor(maxLength / 3);
    return `${str.substring(0, start)}...${str.substring(str.length - end)}`;
  };

  const curveness = useMemo(() => {
    switch (viewType) {
      case "quarterly":
        return 1.02;

      case "monthly":
        return 1.08;

      case "weekly":
        return 1.05;

      default:
        return undefined;
    }
  }, [viewType]);

  const status = task?.status?.toLowerCase?.() || "";
  const statusLabel = getDashboardStatusLabel(status);
  const statusHexColor = isTemplate
    ? "#9ca3af"
    : dashboardStatusHexColors[status] || "#a3a3a3";
  const showRiskTriangle =
    !isTemplate &&
    (status === DashboardStatus.DELAYED ||
      status === DashboardStatus.UNALLOCATED ||
      status === DashboardStatus.BLOCKED);

  return (
    <div style={{ height: GT_taskCardHeight }}>
      <button onClick={() => handleTaskClick(task)}>
        {task?.delayed_duration ? (
          <div
            id={`delayed_task__${currentEntityId}__${task?.id}`}
            className="absolute p-0 rounded-tl rounded-bl bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-400 dark:to-gray-500"
            style={{
              top: index * GT_taskCardHeight,
              width: `${task.delayed_duration * spacing}px`,
              left: delayedTaskPosLeftInPx,
              height: GT_taskCardHeight - GT_progressBarAndTasksGapInPx,
            }}
          >
            <div
              className="absolute w-full p-2 z-10 truncate h-full"
              style={{ width: `${task.delayed_duration * spacing}px` }}
            >
              <TbClock className="absolute top-1 left-1 text-gray-600 dark:text-gray-300" />
              <span className="h-full text-gray-700 dark:text-white dark:opacity-70 flex flex-col align-center justify-center font-normal font-['Inter'] uppercase leading-3 truncate text-sm">
                {task.delayed_duration} Day(s) Delayed
              </span>
            </div>
          </div>
        ) : null}

        <div
          id={`task__${currentEntityId}__${task?.id}`}
          className={`flex absolute bg-white dark:bg-[#1B263B] cursor-pointer border-l-4 border-r-2 border-t-2 border-b-2 rounded-md
          border-r-gray-300 dark:border-r-[#394256] border-b-gray-300 dark:border-b-[#394256] border-t-gray-300 dark:border-t-[#394256] hover:bg-gray-50 dark:hover:bg-[#212e47]
          hover:border-l-6 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_0_0_1px_rgba(99,102,241,0.1),0_0_8px_rgba(99,102,241,0.2)] dark:hover:shadow-[0_8px_16px_rgba(0,0,0,0.5),0_0_0_1px_rgba(99,102,241,0.1),0_0_8px_rgba(99,102,241,0.2)]`}
          style={{
            top: index * GT_taskCardHeight,
            left: taskPosLeftInPx,
            borderLeftColor: statusHexColor,
            borderTopColor: task?.inCriticalPath && "rgb(182, 117, 117)",
            borderTopWidth: task?.inCriticalPath ? 3 : 2,
            borderTopLeftRadius: task?.delayed_duration ? 0 : 4,
            borderBottomLeftRadius: task?.delayed_duration ? 0 : 4,
            width: taskWidth,
            height: GT_taskCardHeight - GT_progressBarAndTasksGapInPx,
            boxShadow: isTaskHighlighted
              ? "0 0 0 1px #FFD70066, 0 0 12px 2px #FFD700"
              : "",
            transition:
              "left 600ms ease-out, width 600ms ease-out, top 600ms ease-out",
            animation: isSimNew ? "newTaskIn 380ms ease-out" : undefined,
          }}
          ref={containerRef}
          onMouseEnter={handleMouseEnter}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <div
            style={{ position: "absolute", left: 0, bottom: 3, width: "100%" }}
          >
            <div
              style={{
                position: "absolute",
                width: `${task?.progress || 0}%`,
                zIndex: 2,
                height: 3,
                backgroundColor: "#4ade80",
              }}
            />
            <div className="absolute w-full h-[3px] bg-gray-200 dark:bg-[#2A3446]" />
          </div>

          {isSimNew && (
            <div
              className={`left-[8px] top-[-14px] px-2 py-1 absolute text-center text-white text-[10px] font-normal font-['Inter'] leading-none bg-green-400 rounded-lg first-letter:uppercase`}
            >
              New
            </div>
          )}

          {isSimUpdated && (
            <div
              className={`left-[8px] top-[-14px] px-2 py-1 absolute text-center text-white text-[10px] font-normal font-['Inter'] leading-none bg-indigo-400 rounded-lg first-letter:uppercase`}
            >
              Updated
            </div>
          )}

          <div className="flex flex-col items-start justify-center gap-1.5 select-none px-1 ml-2 py-2 overflow-hidden hover:overflow-visible relative">
            <div className="flex items-start flex-col w-full">
              {task?.due_date && (
                <div className="w-full  flex items-center justify-start">
                  <span
                    className="text-[10px]  text-gray-600 dark:text-[#8D929D]"
                    style={{
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Due Date: {moment(task.due_date).format("MMM DD")}
                  </span>
                </div>
              )}
              <div
                className="text-gray-900  -mb-0.5 mt-0.5 dark:text-white text-start max-h-[40px] whitespace-pre inline-flex items-center gap-1"
                style={{
                  fontSize: 13,
                  width:
                    workingDaysInTask.length === 1 && task?.member?.image_url
                      ? "75px"
                      : "auto",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {showRiskTriangle && (
                  <span
                    title="This is contributing to risk"
                    className="inline-flex items-center"
                  >
                    <FiAlertTriangle className="text-red-500" />
                  </span>
                )}
                {task.name}
              </div>
            </div>
            <div
              className="flex items-center gap-2 w-full whitespace-pre"
              style={{ fontSize: 9, whiteSpace: "nowrap" }}
            >
              {!isTemplate && (
                <span
                  className="font-semibold"
                  style={{ color: statusHexColor }}
                >
                  {statusLabel}
                  {task?.integration_data?.integration_status &&
                    task.integration_data.integration_status.toLowerCase() !==
                      status &&
                    ` (${task.integration_data.integration_status.charAt(0).toUpperCase() + task.integration_data.integration_status.slice(1).toLowerCase()})`}
                </span>
              )}
              <span className="uppercase text-gray-600 dark:text-[#8D929D]">
                {moment(task.start).format("MMM DD")} -{" "}
                {moment(task.end).format("MMM DD")}
              </span>
            </div>

            {/* Custom Tooltip */}
            {showTooltip && (
              <div
                className={`absolute z-[9999] w-[300px] flex flex-col transform -translate-x-1/2 cursor-auto ${
                  tooltipPosition.placement === "top"
                    ? "bottom-full"
                    : "top-full"
                }`}
                style={{
                  left: `${tooltipPosition.x}px`,
                  animation: "fadeIn 0.2s ease-in-out",
                }}
                onMouseEnter={() => {
                  setIsTooltipHovered(true);
                  // Clear hide timer when hovering over tooltip
                  if (hideTimerRef.current) {
                    clearTimeout(hideTimerRef.current);
                    hideTimerRef.current = null;
                  }
                }}
                onMouseLeave={() => {
                  setIsTooltipHovered(false);
                  // Start hide timer when leaving tooltip
                  hideTimerRef.current = setTimeout(() => {
                    setShowTooltip(false);
                    setIsTooltipHovered(false);
                    hideTimerRef.current = null;
                  }, 200);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <div className="w-full shadow-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-lg overflow-hidden z-50">
                  <EntityViewTooltip
                    taskData={task}
                    isSimulation={simulation}
                    tooltipType="task"
                  />
                </div>

                {/* Tooltip Arrow */}
                <div
                  className={`absolute w-3 h-3 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 transform left-1/2 -translate-x-1/2 ${
                    tooltipPosition.placement === "top"
                      ? "rotate-45 border-r border-b top-full -mt-1.5"
                      : "rotate-45 border-l border-t bottom-full -mb-1.5"
                  }`}
                />
              </div>
            )}
          </div>
          {task?.loss_price ? (
            <div
              className={`absolute top-[-10px] !right-1 z-[9999] h-4 text-[10px] rounded bg-red-600 dark:bg-red-700 text-white flex justify-center items-center p-1 cursor-pointer`}
            >
              {"$"}
            </div>
          ) : null}
        </div>
      </button>
      {task.dependencies &&
        task.dependencies.length > 0 &&
        task.dependencies.map((dependency: string) => (
          <Xarrow
            key={uuid()}
            path={"smooth"}
            start={`task__${currentEntityId}__${dependency}`}
            end={
              task?.delayed_duration === 0
                ? `task__${currentEntityId}__${task.id}`
                : `delayed_task__${currentEntityId}__${task.id}`
            }
            startAnchor={{ position: "bottom", offset: { y: 0 } }}
            endAnchor={{ position: "left", offset: { x: 0 } }}
            headSize={5}
            strokeWidth={2}
            color={task?.skill_color || "#7C3AED"}
            zIndex={0}
            animateDrawing={false}
            showHead={true}
            tailSize={10}
            curveness={curveness}
          />
        ))}
      <style>
        {`
          @keyframes fadeIn {
            from { 
              opacity: 0; 
              transform: translateX(-50%) translateY(${tooltipPosition.placement === "top" ? "5px" : "-5px"}); 
            }
            to { 
              opacity: 1; 
              transform: translateX(-50%) translateY(0); 
            }
          }
          @keyframes newTaskIn {
            0% { opacity: 0; transform: scale(0.95); }
            60% { opacity: 1; transform: scale(1.02); }
            100% { opacity: 1; transform: scale(1); }
          }
        `}
      </style>
    </div>
  );
};

export default GTTask;
